import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, ArrowLeftRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const emptyBranch = { customer_id: "", name: "", address: null };

const BranchPicker = ({ idx, branch, accounts, onChange }) => (
  <div className="space-y-2 rounded-md border border-border p-3" data-testid={`transfer-branch-${idx}`}>
    <Label>Branch {idx + 1} account</Label>
    <Select value={branch.customer_id} onValueChange={(v) => onChange({ ...branch, customer_id: v, name: accounts.find((a) => a.id === v)?.name || "" })}>
      <SelectTrigger data-testid={`transfer-branch-${idx}-account`}><SelectValue placeholder="Pick an account" /></SelectTrigger>
      <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
    </Select>
    <AddressAutocomplete label="Branch address" value={branch.address} initialQuery={branch.address?.label || ""}
      onSelect={(a) => onChange({ ...branch, address: a })} testid={`transfer-branch-${idx}-address`} />
  </div>
);

export const BranchTransferCard = () => {
  const [cfg, setCfg] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/branch-transfer").then((r) => {
      const d = r.data;
      setCfg({ ...d, branches: [d.branches?.[0] || emptyBranch, d.branches?.[1] || emptyBranch] });
    });
    api.get("/quotes/accounts").then((r) => setAccounts(r.data)).catch(() => {});
  }, []);
  if (!cfg) return null;
  const setBranch = (i, b) => setCfg((c) => ({ ...c, branches: c.branches.map((x, j) => (j === i ? b : x)) }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/branch-transfer", cfg);
      setCfg({ ...data, branches: [data.branches[0], data.branches[1]] });
      toast.success("Branch transfer pricing saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Card data-testid="branch-transfer-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><ArrowLeftRight className="h-5 w-5 text-kilted-gold" /> Branch transfers (quick button)</CardTitle>
        <p className="text-sm text-muted-foreground">Each of these two accounts gets a "Transfer to …" button that fills in both branch addresses. Normal account pricing applies.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2"><Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="transfer-enabled" /><Label>On</Label></div>
          <Input value={cfg.name} onChange={(e) => setCfg({ ...cfg, name: e.target.value })} className="max-w-xs" data-testid="transfer-name" />
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {cfg.branches.map((b, i) => <BranchPicker key={i} idx={i} branch={b} accounts={accounts} onChange={(nb) => setBranch(i, nb)} />)}
        </div>
        <Button onClick={save} disabled={busy} data-testid="transfer-save-button" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save branch transfers
        </Button>
      </CardContent>
    </Card>
  );
};
